import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, Outlet } from 'react-router-dom';

import wordmarkHorizontal from '@/assets/logo/wordmark-horizontal.svg';

import { useTheme } from '@/hooks/useTheme';
import { usePageView } from '@/hooks/use-page-view';
import { AuthGuard } from '@/components/auth/AuthGuard';
// grep audit: syncService.start, loadActiveProperty, notificationService.init must NOT
// appear inside AuthBootstrap or its imports — those side-effects belong to AppShell only.
import { AuthBootstrap } from '@/components/auth/AuthBootstrap';
import { useAuthStore } from '@/stores/auth-store';
import { useSyncStore } from '@/stores/sync-store';
import { useActivePropertyStore } from '@/stores/active-property-store';
import { cleanupInvalidRecords } from '@/services/data-cleanup-service';
import { checkAndNotify } from '@/services/notification-scheduler';
import { setPhotoHydratedCallback } from '@/services/photo-sync-service';
import { SyncStatusBadge } from '@/components/sync/SyncStatusBadge';
import { SyncStatusDrawer } from '@/components/sync/SyncStatusDrawer';
import { InitialSyncBoundary } from '@/components/sync/InitialSyncBoundary';
import { OfflineBanner } from '@/components/common/OfflineBanner';
import { BottomNav } from '@/components/nav/BottomNav';
import { ScrollToTop } from '@/components/common/ScrollToTop';

import { DashboardPage } from '@/components/dashboard/DashboardPage';
import PropertiesPage from '@/components/property/PropertiesPage';
import PropertyDetailPage from '@/components/property/PropertyDetailPage';
import WaypointDetailPage from '@/components/property/WaypointDetailPage';
import MapPage from '@/components/map/MapPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { ProfilePage as SettingsProfilePage } from '@/pages/settings/ProfilePage';
import { PreferencesPage } from '@/pages/settings/PreferencesPage';
import { PropertyFormPage } from '@/pages/PropertyFormPage';
import { SeasonsPage } from '@/pages/SeasonsPage';
import { SeasonFormPage } from '@/pages/SeasonFormPage';
import { SeasonDetailPage } from '@/pages/SeasonDetailPage';
import { TemplatesPage } from '@/pages/TemplatesPage';
import { TemplateDetailPage } from '@/pages/TemplateDetailPage';
import { TemplateFormPage } from '@/pages/TemplateFormPage';
import { TaskListPage } from '@/pages/TaskListPage';
import { TaskDetailPage } from '@/pages/TaskDetailPage';
import { TaskFormPage } from '@/pages/TaskFormPage';
import { LogEntryFormPage } from '@/pages/LogEntryFormPage';
import { LogEntryDetailPage } from '@/pages/LogEntryDetailPage';
import { WaypointComparisonPage, PropertyComparisonPage } from '@/pages/ComparisonPage';
import { ShoppingListPage } from '@/pages/ShoppingListPage';
import { SharedPropertyPage } from '@/pages/SharedPropertyPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { RedeemBetaPage } from '@/pages/RedeemBetaPage';
import { BetaInvitesPage } from '@/pages/admin/BetaInvitesPage';
import { AdminRoute } from '@/components/admin/AdminRoute';
import { HuntListPage } from '@/pages/HuntListPage';
import { HuntNewPage } from '@/pages/HuntNewPage';
import { HuntDetailPage } from '@/pages/HuntDetailPage';
import { HuntEditPage } from '@/pages/HuntEditPage';
import { WaypointFormPage } from '@/pages/WaypointFormPage';
import { HarvestFormPage } from '@/pages/HarvestFormPage';
import { HarvestCelebrationPage } from '@/pages/HarvestCelebrationPage';
import { SeasonArchivePage } from '@/pages/SeasonArchivePage';
import { ForecastPage } from '@/pages/ForecastPage';
import { TrailDrawPage } from '@/pages/TrailDrawPage';
import { MorePage } from '@/pages/MorePage';
import { HarvestGalleryPage } from '@/pages/HarvestGalleryPage';
import { ScoutLogPage } from '@/pages/ScoutLogPage';
import { CamerasPage } from '@/pages/CamerasPage';
import { PatternsPage } from '@/pages/PatternsPage';
import { AboutPage } from '@/pages/AboutPage';
import SubscriptionPage from '@/pages/SubscriptionPage';

// Public legal pages (M72) — lazy-loaded; they import committed markdown via `?raw`
// and render outside AuthGuard.
const PrivacyPolicyPage = lazy(() =>
  import('@/pages/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })),
);
const TermsOfServicePage = lazy(() =>
  import('@/pages/TermsOfServicePage').then((m) => ({ default: m.TermsOfServicePage })),
);

// Full-height dark placeholder for the lazy legal routes — no white flash on the
// bark-900 surface (M72 W3).
function LegalRouteFallback() {
  return <div className="min-h-dvh bg-bark-900" data-testid="legal-route-fallback" />;
}

// DEV-only: error trigger for E2E testing of the Sentry ErrorBoundary.
// Tree-shaken in production builds by Vite (import.meta.env.DEV is false).
import { ErrorTrigger } from '@/components/dev/ErrorTrigger';

// ── Redirect helpers for old routes with dynamic segments ─────────────────────

function RedirectPropertyDetail() {
  const { propertyId } = useParams<{ propertyId: string }>();
  return <Navigate to={`/land/properties/${propertyId ?? ''}`} replace />;
}

function RedirectPropertyLocation() {
  const { propertyId, locationId } = useParams<{ propertyId: string; locationId: string }>();
  return (
    <Navigate
      to={`/land/properties/${propertyId ?? ''}/waypoints/${locationId ?? ''}`}
      replace
    />
  );
}

function RedirectPropertyWaypoint() {
  const { propertyId, waypointId } = useParams<{ propertyId: string; waypointId: string }>();
  return (
    <Navigate
      to={`/land/properties/${propertyId ?? ''}/waypoints/${waypointId ?? ''}`}
      replace
    />
  );
}

function RedirectSeasonTasks() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/prep/tasks?season=${id ?? ''}`} replace />;
}

function RedirectSeasonTaskDetail() {
  const { id, taskId } = useParams<{ id: string; taskId: string }>();
  return <Navigate to={`/prep/tasks/${taskId ?? ''}?season=${id ?? ''}`} replace />;
}

function RedirectSeasonTaskNew() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/prep/tasks/new${id ? `?season=${id}` : ''}`} replace />;
}

function RedirectLocationLog() {
  const { locationId } = useParams<{ locationId: string }>();
  return <Navigate to={`/scout/log?location=${locationId ?? ''}`} replace />;
}

// Layout for all non-map routes — padded, max-width constrained, scrollable
function DefaultLayout() {
  return (
    // hypothesis (fallback artifacts) — verify on device: overflow-x-hidden prevents iOS Safari PWA from picking a >device-width initial-scale when a descendant overflows horizontally (L-03)
    <main
      className="h-full overflow-y-auto overflow-x-hidden px-4 py-6 max-w-2xl mx-auto w-full"
      style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom) + 24px)' }}
    >
      <Outlet />
    </main>
  );
}

// Full-bleed layout for pages that own their own internal padding (e.g. TaskListPage)
function FullBleedLayout() {
  return (
    <main
      className="h-full overflow-y-auto max-w-2xl mx-auto w-full"
      style={{ paddingBottom: 'calc(56px + env(safe-area-inset-bottom) + 24px)' }}
    >
      <Outlet />
    </main>
  );
}

function AppShell() {
  useTheme();
  usePageView();
  const { user, status, preferences } = useAuthStore();
  const { initSync, syncNow, decrementPhotoHydrationCount, hasPushedFirstUpsert } = useSyncStore();
  const { initActiveProperty } = useActivePropertyStore();
  const [syncDrawerOpen, setSyncDrawerOpen] = useState(false);

  // Wire photo-hydrated callback once on mount so downloadPhoto() can decrement
  // the InitialSyncBoundary counter without importing sync-store (RULE-081).
  useEffect(() => {
    setPhotoHydratedCallback(decrementPhotoHydrationCount);
    return () => {
      setPhotoHydratedCallback(null);
    };
    // decrementPhotoHydrationCount is stable (Zustand action) — no re-registration needed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize sync after auth resolves, then re-sync on foreground/network events
  useEffect(() => {
    if (status !== 'authenticated' || !user) return;

    initSync(user.id);
    initActiveProperty();

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void syncNow(user!.id);
      }
    }

    function handleOnline() {
      void syncNow(user!.id);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user?.id]);

  // Check and fire local push notifications on app foreground
  useEffect(() => {
    if (status !== 'authenticated' || !preferences?.notificationsEnabled) return;

    const leadDays = preferences.notificationLeadDays ?? 3;

    void checkAndNotify(leadDays);

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        void checkAndNotify(leadDays);
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [status, preferences?.notificationsEnabled, preferences?.notificationLeadDays]);

  return (
    <div className="h-dvh bg-[var(--color-bg)] text-[var(--color-text-primary)] flex flex-col">
      {/* ── Header ── */}
      <header className="bg-bark-900 border-b border-bark-700 px-4 min-h-16 flex items-center justify-between sticky top-0 z-10 w-full">
        <img src={wordmarkHorizontal} alt="Ridgen" className="h-14 w-auto" />
        {/* Tap the sync badge to open the sync status drawer */}
        <button
          type="button"
          onClick={() => setSyncDrawerOpen(true)}
          aria-label="Open sync status"
          data-testid="sync-badge-trigger"
          className="flex items-center"
        >
          <SyncStatusBadge />
        </button>
      </header>

      {/* F1.2 — readiness sentinel for the liveStagingTest storage-state phase.
          ADR-042 §Layer 3 readiness-sentinel pattern (precedent: src/pages/HuntDetailPage.tsx:148,
          src/pages/HarvestCelebrationPage.tsx:98 — eight existing route-ready instances).
          Standalone-leaf shape (no wrapped content) because the sentinel signals a store
          predicate, not a route-loaded predicate. Renders OUTSIDE InitialSyncBoundary at :233
          so it is observable to Playwright during the sign-in storage-state phase, BEFORE
          initial sync resolves.

          data-status intentionally omitted: this sentinel's gate is the fire-once
          `hasPushedFirstUpsert` boolean, not authStatus — analogous to src/pages/HuntDetailPage.tsx:145-146
          ("data-status intentionally omitted: HuntDetailPage's gate is data-loader state, not authStatus").
          Conditional presence IS the contract. */}
      {hasPushedFirstUpsert && <div data-testid="sync-initial-user-pushed" />}

      <OfflineBanner />

      {/* ── Content area — fills space between header/banner and nav ── */}
      <div className="flex-1 min-h-0">
        <InitialSyncBoundary>
        <Routes>
          {/* ── Map — full-bleed, no padding wrapper ── */}
          <Route path="/map" element={<MapPage />} />

          {/* ── Trail drawing — full-screen, no DefaultLayout, covers BottomNav via fixed overlay ── */}
          <Route path="/locations/draw-trail" element={<TrailDrawPage />} />
          <Route path="/locations/:id/draw-trail" element={<TrailDrawPage />} />

          {/* ── Full-bleed pages — manage their own internal padding ── */}
          <Route element={<FullBleedLayout />}>
            <Route path="/prep/tasks" element={<TaskListPage />} />
          </Route>

          {/* ── All other routes — padded, scrollable via DefaultLayout ── */}
          <Route element={<DefaultLayout />}>
          {/* ── Home ── */}
          <Route path="/" element={<DashboardPage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* ── Forecast ── */}
          <Route path="/forecast" element={<ForecastPage />} />

          {/* ── Scout ── */}
          <Route path="/scout" element={<Navigate to="/scout/log" replace />} />
          <Route path="/scout/log" element={<ScoutLogPage />} />
          <Route path="/scout/log/new" element={<LogEntryFormPage />} />
          <Route path="/scout/log/:entryId/edit" element={<LogEntryFormPage />} />
          <Route path="/scout/log/:entryId" element={<LogEntryDetailPage />} />
          <Route path="/scout/cameras" element={<CamerasPage />} />
          <Route path="/scout/patterns" element={<PatternsPage />} />

          {/* ── Prep ── */}
          <Route path="/prep" element={<Navigate to="/prep/tasks" replace />} />
          <Route path="/prep/tasks/new" element={<TaskFormPage />} />
          <Route path="/prep/tasks/:taskId/edit" element={<TaskFormPage />} />
          <Route path="/prep/tasks/:taskId" element={<TaskDetailPage />} />
          <Route path="/prep/templates" element={<TemplatesPage />} />
          <Route path="/prep/templates/new" element={<TemplateFormPage />} />
          <Route path="/prep/templates/:id/edit" element={<TemplateFormPage />} />
          <Route path="/prep/templates/:id" element={<TemplateDetailPage />} />
          <Route path="/prep/shopping" element={<ShoppingListPage />} />

          {/* ── More ── */}
          <Route path="/more" element={<MorePage />} />
          <Route path="/more/harvests" element={<HarvestGalleryPage />} />

          {/* ── Land (properties) ── */}
          <Route path="/land/properties" element={<PropertiesPage />} />
          <Route path="/land/properties/new" element={<PropertyFormPage />} />
          <Route path="/land/properties/:propertyId/edit" element={<PropertyFormPage />} />
          <Route path="/land/properties/:propertyId/waypoints/new" element={<WaypointFormPage />} />
          <Route path="/land/properties/:propertyId" element={<PropertyDetailPage />} />
          <Route
            path="/land/properties/:propertyId/waypoints/:waypointId"
            element={<WaypointDetailPage />}
          />
          <Route
            path="/land/properties/:propertyId/waypoints/:waypointId/compare"
            element={<WaypointComparisonPage />}
          />
          <Route
            path="/land/properties/:propertyId/compare"
            element={<PropertyComparisonPage />}
          />

          {/* ── Seasons ── */}
          <Route path="/seasons" element={<SeasonsPage />} />
          <Route path="/seasons/new" element={<SeasonFormPage />} />
          <Route path="/seasons/:id" element={<SeasonDetailPage />} />
          <Route path="/seasons/:id/edit" element={<SeasonFormPage />} />
          <Route path="/seasons/:id/archive" element={<SeasonArchivePage />} />

          {/* ── Hunt Journal ── */}
          <Route path="/hunt" element={<HuntListPage />} />
          <Route path="/hunt/new" element={<HuntNewPage />} />
          <Route path="/hunt/:huntId/harvest/new" element={<HarvestFormPage />} />
          <Route path="/hunt/:huntId/harvest/:harvestId/edit" element={<HarvestFormPage />} />
          <Route path="/hunt/:huntId/harvest/:harvestId/celebrate" element={<HarvestCelebrationPage />} />
          <Route path="/hunt/:id/edit" element={<HuntEditPage />} />
          <Route path="/hunt/:id" element={<HuntDetailPage />} />

          {/* ── Profile / Settings ── */}
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/settings" element={<Navigate to="/settings/profile" replace />} />
          <Route path="/settings/profile" element={<SettingsProfilePage />} />
          <Route path="/settings/preferences" element={<PreferencesPage />} />

          {/* ── Misc pages ── */}
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/about" element={<AboutPage />} />

          {/* ── Beta invite redemption (post-auth handoff route, M58) ──
              `/invite/:token` is the public landing (registered outside AuthGuard
              below); `/redeem?token=…` is the authenticated redemption step.
              RedeemBetaPage handles both routes via mode detection on params. */}
          <Route path="/redeem" element={<RedeemBetaPage />} />

          {/* ── Admin (M58) ── */}
          <Route
            path="/admin/beta-invites"
            element={
              <AdminRoute>
                <BetaInvitesPage />
              </AdminRoute>
            }
          />

          {/* ── Old route redirects (preserve deep links) ── */}
          <Route path="/dashboard" element={<Navigate to="/" replace />} />
          <Route path="/properties" element={<Navigate to="/land/properties" replace />} />
          <Route path="/properties/new" element={<Navigate to="/land/properties/new" replace />} />
          <Route path="/properties/:propertyId/edit" element={<RedirectPropertyDetail />} />
          <Route path="/properties/:propertyId" element={<RedirectPropertyDetail />} />
          <Route
            path="/properties/:propertyId/locations/:locationId"
            element={<RedirectPropertyLocation />}
          />
          <Route
            path="/properties/:propertyId/waypoints/:waypointId"
            element={<RedirectPropertyWaypoint />}
          />
          <Route path="/templates" element={<Navigate to="/prep/templates" replace />} />
          <Route path="/shopping" element={<Navigate to="/prep/shopping" replace />} />
          <Route path="/seasons/:id/tasks" element={<RedirectSeasonTasks />} />
          <Route path="/seasons/:id/tasks/new" element={<RedirectSeasonTaskNew />} />
          <Route
            path="/seasons/:id/tasks/:taskId"
            element={<RedirectSeasonTaskDetail />}
          />
          <Route path="/checklist" element={<Navigate to="/prep/tasks" replace />} />
          <Route path="/locations/:locationId/log" element={<RedirectLocationLog />} />
          <Route
            path="/locations/:locationId/log/new"
            element={<LogEntryFormPage />}
          />
          <Route
            path="/locations/:locationId/log/:entryId/edit"
            element={<LogEntryFormPage />}
          />
          <Route
            path="/locations/:locationId/log/:entryId"
            element={<LogEntryDetailPage />}
          />
          <Route
            path="/waypoints/:waypointId/log/new"
            element={<LogEntryFormPage />}
          />
          <Route
            path="/waypoints/:waypointId/log/:entryId/edit"
            element={<LogEntryFormPage />}
          />
          <Route
            path="/waypoints/:waypointId/log/:entryId"
            element={<LogEntryDetailPage />}
          />
          {/* ── Catch-all: redirect unknown paths (e.g. /auth after login) to dashboard ── */}
          <Route path="*" element={<Navigate to="/" replace />} />
          </Route>{/* end DefaultLayout */}
        </Routes>
        </InitialSyncBoundary>
      </div>

      {/* TODO M-AI-Chat: AI Chat FAB — floating button, bottom-right, above BottomNav */}

      {/* DEV-only: error trigger for E2E testing of the Sentry ErrorBoundary */}
      {import.meta.env.DEV && <ErrorTrigger />}

      {/* App-level sync drawer — opened by header badge tap */}
      <SyncStatusDrawer open={syncDrawerOpen} onClose={() => setSyncDrawerOpen(false)} />

      {/* ── Bottom navigation ── */}
      <BottomNav />
    </div>
  );
}

// Run data integrity cleanup once at module load — before auth, before render
void cleanupInvalidRecords();

export function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      {/* F12.1 Path 2: AuthBootstrap subscribes the auth listener for ALL routes,
          including public routes that bypass AuthGuard (/invite/:token,
          /shared/:token, /auth/callback). Without it, public-route users never
          see Zustand `status` resolve from 'loading' → 'authenticated', which
          short-circuits authenticated-user branches like the
          masks-match-confirm UX on /invite/:token. */}
      <AuthBootstrap />
      <Routes>
        {/* Public routes — no auth required. Must be outside AuthGuard. */}
        <Route path="/shared/:token" element={<SharedPropertyPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* M58: Beta invite landing — public, anonymous-allowed. The page
            calls validate-beta-invite and either renders an error view or
            prompts sign-in. ADR-038 Decision 1. */}
        <Route path="/invite/:token" element={<RedeemBetaPage />} />
        {/* M72: Public legal pages — no auth required, outside AuthGuard. Lazy-loaded
            with a bark-900 Suspense fallback so there is no white flash. */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LegalRouteFallback />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LegalRouteFallback />}>
              <TermsOfServicePage />
            </Suspense>
          }
        />
        {/* All other routes require authentication */}
        <Route
          path="*"
          element={
            <AuthGuard>
              <AppShell />
            </AuthGuard>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
